import { useState, useEffect } from "react";
import { LayoutDashboard, UserCog, Briefcase, Users, MessageSquare, BookOpen, Link2, Code, Image, FileText, Settings, Palette, Search, Shield, BarChart3, Trophy, Target, Download, Rocket, Megaphone, Gift, Bot, Wand2, Footprints, Database, Handshake, Newspaper, Headphones, Store, Award, Vote, Mail } from "lucide-react";
import { useLocation } from "react-router-dom";
import { EditProfileDialog } from "./EditProfileDialog";
import { useToast } from "../ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { SidebarMenuItem } from "./sidebar/SidebarMenuItem";
import { SidebarSubmenu } from "./sidebar/SidebarSubmenu";
import { AddLinkDialog } from "./sidebar/AddLinkDialog";
import { EditLinkDialog } from "./sidebar/EditLinkDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIsAdmin } from "@/hooks/useUserRole";
import { useNewFeatureBadge } from "@/hooks/useNewFeatureBadge";
import { useHasCertificates } from "@/hooks/useHasCertificates";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";

interface SubMenuItem {
  id: string;
  title: string;
  url: string;
}

interface CandidateSidebarProps {
  isMobileSheet?: boolean;
}

// Definição de quais features devem mostrar badge "NOVO"
const NEW_FEATURES = ['comece-aqui', 'academy', 'datasets', 'feed', 'desafios'] as const;
type FeatureKey = typeof NEW_FEATURES[number];

export function CandidateSidebar({ isMobileSheet = false }: CandidateSidebarProps) {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isAddLinkOpen, setIsAddLinkOpen] = useState(false);
  const [isEditLinkOpen, setIsEditLinkOpen] = useState(false);
  const [linkToEdit, setLinkToEdit] = useState<SubMenuItem | null>(null);
  const { isAdmin } = useIsAdmin();
  const [internalLinks, setInternalLinks] = useState<SubMenuItem[]>([]);
  const { toast } = useToast();
  const { shouldShowBadge, markAsViewed } = useNewFeatureBadge();
  const { data: certInfo } = useHasCertificates();
  const { isEnabled: academyEnabled } = useFeatureFlag("academy_enabled");

  // Active poll badge: show "Enquete" badge on Comece Aqui when there is an active poll the user hasn't voted on
  const { data: activePoll } = useQuery({
    queryKey: ["platform-poll", "active"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("get_active_platform_poll");
      if (error) return null;
      return data && data.length > 0 ? data[0] : null;
    },
    staleTime: 60_000,
  });
  const showPollBadge = !!activePoll && !activePoll.user_voted_option_id;

  // Check if there are active partner pages
  const { data: activePartnerPages = [] } = useQuery({
    queryKey: ["active-partner-pages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partner_landing_pages")
        .select("slug, page_title")
        .eq("is_active", true);
      if (error) return [];
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Check if there are active datasets
  const { data: datasetsCount = 0 } = useQuery({
    queryKey: ["datasets-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("community_datasets" as any)
        .select("*", { count: "exact", head: true })
        .eq("is_active", true);
      if (error) return 0;
      return count || 0;
    },
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    fetchInternalLinks();
  }, []);

  const fetchInternalLinks = async () => {
    const { data, error } = await supabase
      .from('community_external_links')
      .select('*')
      .order('order_index');

    if (data) {
      setInternalLinks(data);
    }
  };

  const handleAddLink = () => {
    setIsAddLinkOpen(true);
  };

  const handleEditLink = async (item: SubMenuItem) => {
    setLinkToEdit(item);
    setIsEditLinkOpen(true);
  };

  const handleDeleteLink = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir este link?")) {
      return;
    }

    try {
      const { error } = await supabase
        .from('community_external_links')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await fetchInternalLinks();
      toast({
        title: "Link excluído",
        description: "O link foi removido com sucesso",
      });
    } catch (error) {
      console.error('Erro ao deletar link:', error);
      toast({
        title: "Erro ao excluir",
        description: "Não foi possível excluir o link",
        variant: "destructive",
      });
    }
  };


  // Menu items base
  const baseMenuItems = [
    {
      icon: Rocket,
      label: "Comece Aqui",
      path: "/comece-aqui",
      featureKey: "comece-aqui" as FeatureKey
    },
    {
      icon: LayoutDashboard,
      label: "Dashboard",
      path: "/candidate/dashboard"
    },
    {
      icon: Briefcase,
      label: "Vagas",
      path: "/candidate/jobs"
    },
    {
      icon: Trophy,
      label: "Conquistas",
      path: "/candidate/achievements"
    },
    ...(certInfo?.hasAny ? [{
      icon: Award,
      label: "Certificados",
      path: "/candidate/certificates"
    }] : []),
    {
      icon: Gift,
      label: "Convites",
      path: "/candidate/referrals"
    },
    {
      icon: Code,
      label: "Desafios",
      path: "/candidate/challenges",
      featureKey: "desafios" as FeatureKey
    }
  ];

  // Academy: sempre para admin, e para candidatos quando a flag estiver ativa
  const menuItems = (isAdmin || academyEnabled)
    ? [
        ...baseMenuItems,
        {
          icon: Target,
          label: "Academy",
          path: "/candidate/practice",
          featureKey: "academy" as FeatureKey
        }
      ]
    : baseMenuItems;

  const communityItems = [
    {
      icon: Newspaper,
      label: "Feed",
      path: "/candidate/jobs/community/feed",
      featureKey: "feed" as FeatureKey
    },
    {
      icon: Users,
      label: "Apresente-se",
      path: "/candidate/jobs/community/introductions"
    },
    {
      icon: BookOpen,
      label: "Conhecimento Técnico",
      path: "/candidate/jobs/community/learning"
    },
    {
      icon: MessageSquare,
      label: "Carreira & Vagas",
      path: "/candidate/jobs/community/questions"
    },
    // Datasets: only show if admin or there are active datasets
    ...((isAdmin || datasetsCount > 0) ? [{
      icon: Database,
      label: "Datasets",
      path: "/candidate/jobs/community/datasets",
      featureKey: "datasets" as FeatureKey,
    }] : []),
    // Partner pages: show only when active
    ...activePartnerPages.map(p => ({
      icon: Handshake,
      label: p.page_title || "Parceiros",
      path: `/candidate/jobs/community/parceiros/${p.slug}`,
      featureKey: `partner-${p.slug}`,
    })),
  ];

  const adminItems = [
    {
      icon: Briefcase,
      label: "Vagas Curadoria",
      path: "/candidate/admin/curated-jobs"
    },
    {
      icon: Download,
      label: "Importação LinkedIn",
      path: "/candidate/admin/linkedin-imports"
    },
    {
      icon: Mail,
      label: "Emails / Digest",
      path: "/candidate/admin/job-digest"
    },
    {
      icon: Code,
      label: "Traqueamento",
      path: "/candidate/admin/seo-scripts"
    },
    {
      icon: Image,
      label: "Banners",
      path: "/candidate/admin/banners"
    },
    {
      icon: FileText,
      label: "Páginas",
      path: "/candidate/admin/static-pages"
    },
    {
      icon: Settings,
      label: "Contato",
      path: "/candidate/admin/contact-settings"
    },
    {
      icon: Palette,
      label: "Interface",
      path: "/candidate/admin/ui-settings"
    },
    {
      icon: Search,
      label: "SEO",
      path: "/candidate/admin/seo-settings"
    },
    {
      icon: Shield,
      label: "Teste de Roles",
      path: "/candidate/admin/role-test"
    },
    {
      icon: BarChart3,
      label: "Analytics",
      path: "/candidate/admin/analytics"
    },
    {
      icon: Download,
      label: "Exportar Guias",
      path: "/candidate/admin/guides-export"
    },
    {
      icon: Megaphone,
      label: "Anúncios",
      path: "/candidate/admin/announcements"
    },
    {
      icon: Bot,
      label: "Tutor IA",
      path: "/candidate/admin/tutor-analytics"
    },
    {
      icon: Wand2,
      label: "Gerador de Questões",
      path: "/candidate/admin/quiz-generator"
    },
    {
      icon: Target,
      label: "Gestor Academy",
      path: "/candidate/admin/academy-manager"
    },
    {
      icon: Footprints,
      label: "Rodapé",
      path: "/candidate/admin/footer-settings"
    },
    {
      icon: Handshake,
      label: "LP Parceiros",
      path: "/candidate/admin/partner-pages"
    },
    {
      icon: Headphones,
      label: "Suporte & Feedbacks",
      path: "/candidate/admin/support-feedbacks"
    },
    {
      icon: Store,
      label: "LP Empresas",
      path: "/candidate/admin/sales-page"
    },
    {
      icon: Vote,
      label: "Enquetes",
      path: "/candidate/admin/polls"
    }
  ];

  // Classes condicionais baseadas no contexto (mobile sheet vs desktop sidebar)
  // Desktop: fixed sidebar; Mobile sheet: full width for drawer/sheet usage
  const containerClasses = isMobileSheet
    ? "w-full bg-gray-50 h-full overflow-y-auto"
    : "w-64 bg-gray-50 border-r fixed top-16 h-[calc(100vh-4rem)] overflow-y-auto";

  const navClasses = isMobileSheet
    ? "space-y-4 p-4 pb-6"
    : "space-y-6 p-4";

  const sectionSpacing = isMobileSheet
    ? "space-y-3"
    : "space-y-2";

  const headerClasses = isMobileSheet
    ? "text-sm font-semibold text-gray-600 uppercase tracking-wider px-1"
    : "text-sm font-semibold text-gray-500 uppercase tracking-wider";

  return (
    <aside className={containerClasses}>
      <nav className={navClasses}>
        <div className={sectionSpacing}>
          <h2 className={headerClasses}>
            Menu Principal
          </h2>
          {menuItems.map((item) => {
            const isComeceAqui = item.path === "/comece-aqui";
            const newBadge = item.featureKey && shouldShowBadge(item.featureKey) ? "NOVO" : undefined;
            const badge = isComeceAqui && showPollBadge ? "ENQUETE" : newBadge;
            return (
              <SidebarMenuItem
                key={item.path}
                {...item}
                isActive={location.pathname === item.path}
                badge={badge}
                onView={markAsViewed}
              />
            );
          })}
        </div>

        <div className={sectionSpacing}>
          <h2 className={headerClasses}>
            Comunidade
          </h2>
          {communityItems.map((item) => (
            <SidebarMenuItem
              key={item.path}
              {...item}
              isActive={location.pathname === item.path}
              badge={'featureKey' in item && item.featureKey && shouldShowBadge(item.featureKey, item.featureKey === 'datasets' ? 3 : undefined) ? "NOVO" : undefined}
              onView={markAsViewed}
            />
          ))}

          <SidebarSubmenu
            isAdmin={isAdmin}
            items={internalLinks}
            onAddItem={handleAddLink}
            onEditItem={handleEditLink}
            onDeleteItem={handleDeleteLink}
          />
        </div>

        {isAdmin && (
          <div className={sectionSpacing}>
            <h2 className={headerClasses}>
              Administração
            </h2>
            {adminItems.map((item) => (
              <SidebarMenuItem
                key={item.path}
                {...item}
                isActive={location.pathname === item.path}
              />
            ))}
          </div>
        )}

        <div className={sectionSpacing}>
          <button
            onClick={() => setIsEditProfileOpen(true)}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-gray-600 hover:bg-gray-100 transition-colors ${isMobileSheet ? "text-sm" : "text-xs"
              }`}
          >
            <UserCog className="h-4 w-4" />
            <span>Editar Perfil</span>
          </button>
        </div>
      </nav>

      <EditProfileDialog
        open={isEditProfileOpen}
        onOpenChange={setIsEditProfileOpen}
      />

      <AddLinkDialog
        open={isAddLinkOpen}
        onOpenChange={setIsAddLinkOpen}
        onSuccess={fetchInternalLinks}
      />

      <EditLinkDialog
        open={isEditLinkOpen}
        onOpenChange={setIsEditLinkOpen}
        onSuccess={fetchInternalLinks}
        linkToEdit={linkToEdit}
      />
    </aside>
  );
}
